import React, { useCallback, useContext, useMemo, useRef } from "react";
import { shallowEqual, useSelector } from "react-redux";
import type { RootState } from "state/rootReducer";
import {
  Box,
  Heading,
  useColorModeValue,
  Stack,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
  SlideFade,
  IconButton,
  Tag,
  Icon,
  Center,
  Button,
  Image,
  Menu,
  MenuButton,
  Portal,
  MenuList,
  Text,
} from "@chakra-ui/react";
import {
  useButtonProps,
  useCollectionKey,
  useCollectionQuestionsFromReduxStore,
  useConfigMap,
  useContents,
  useDashboardMetrics,
  useFilterAndGroupReports,
  useLatestCollectionWorkflowId,
  useProjectInPortfolios,
  useProjectParams,
  useTileProps,
  useUserProfile,
} from "hooks";
import { BiDotsVertical, BiLike, BiShareAlt } from "react-icons/bi";
import { SmallActionButton } from "screens/content/contentCanvas/cell/SmallActionButton";
import { MarkdownItemView } from "screens/markdown/MarkdownItemView";
import { StockEquityChart } from "screens/collection/components/StockEquityChart";
import { formatDateWithOutputFormat } from "screens/common/modal/formatters";
import { useCustomScrollbar } from "hooks/useCustomScrollbar";
import { ProjectHeaderImage } from "./components/ProjectHeaderImage";
import { CollectionsFilterContext } from "../../collections";
import { useNavigate } from "react-router-dom";
import { useConversationContext } from "screens/thread/ConversationContext";
import { useSettingContext } from "screens/panels/settings/SettingsProvider";
import { WorkflowProgressBar } from "screens/landing/components/WorkflowProgressBar";
import { useCopyValue } from "hooks/useCopies";
import { useWorkflowCompletionDateOrNow, useWorkflowKey } from "hooks/useWorkflows";
import { formatDistanceToNow } from "date-fns";
import { ProjectDeleteButton } from "screens/collection/views/ProjectActions/ProjectDeleteButton";
import { ProjectRerunCancelButton } from "screens/collection/views/ProjectActions/ProjectRerunCancelButton";
import { MoveProjectModal } from "screens/collection/views/ProjectActions/MoveProjectModal";
import { v4 as uuid } from "uuid";
import { ProjectMoveButton } from "screens/collection/views/ProjectActions/ProjectMoveButton";
import { useProjectMove } from "hooks/useProjectMove";
import { CloseIcon } from "@chakra-ui/icons";
import { TbVersions } from "react-icons/tb";
import { WorkflowProgressStepperContainer } from "screens/landing/components/WorkflowProgressStepper";
import { useWorkflowScheduleByProject } from "hooks/useWorkflowSchedules";
import { TextOverflowTooltip } from "screens/landing/components/TextOverflowTooltip";
import { FiShare2 } from "react-icons/fi";
import { GrLineChart } from "react-icons/gr";
import { ProjectReportPanelButton } from "screens/collection/components/ProjectReportPanelButton";
import { ContentPreview } from "screens/content/contentView/previewSection/ContentPreview";
import type { ContentDetails } from "types/content/ContentDetails";
import { ProjectAnalysisPopover } from "../../collections/chartComponents/ProjectAnalysisPopover";
import type { StockEquityData } from "types/stock";
import { generateMockStockEquityData } from "types/stock";
import { ShowMoreContainer } from "screens/common/components/ShowMoreContainer";
import dealsIcon from "screens/common/static/misc/deals_icon.svg";
import { getViewConfig } from "configs/configMap";
import { createRatingConfig, getRatingInfo, parseBuySellRatingNumber } from "../../collections/chartComponents/data/buySellData";
import { useBuySellChartColors } from "../../collections/chartComponents/data/buySellData";
import { StockIndicatorSmall } from "./components/StockIndicatorSmall";
import type { DashboardMetric } from "types/dashboardMetrics";

interface ProjectLandingSmallTileProps {
  collectionId: string;
  previousVersions?: React.ReactNode;
  previousVersionCount?: number;
  companyDescription?: string;
  logoUrl?: string;
}

const ProjectLandingSmallTile: React.FC<ProjectLandingSmallTileProps> = ({
  collectionId,
  previousVersions,
  previousVersionCount,
  companyDescription,
  logoUrl,
}) => {
  const { projectFilter, parentRoute, isPortfolios } = useProjectParams();
  const tileBgColor = useColorModeValue("white", "#161B25");
  const tileBorderColor = useColorModeValue("gray.300", "gray.700");
  const tagBgColor = useColorModeValue("white", "gray.800");
  const tagBorderColor = useColorModeValue("gray.300", "gray.700");
  const tagColor = useColorModeValue("gray.600", "gray.500");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("primary.darkGray", "gray.300");
  const defaultIconColor = useColorModeValue("gray.500", "gray.400");
  const panelBgColor = useColorModeValue("white", "gray.800");

  const primaryButtonProps = useButtonProps("md", "primary");
  const commonTileProps = useTileProps();
  const isMobile = useBreakpointValue({ base: true, md: false, lg: false }, { fallback: "md", ssr: false });
  const navigate = useNavigate();

  const collection = useSelector(
    (state: RootState) => (collectionId ? state.collection.collections[collectionId] : undefined),
    shallowEqual
  );

  const projectName = collection?.name;
  const projectMetadataIds = collection?.metadataIds;
  const collectionType = collection?.collectionType;
  const projectGroupId = collection?.projectGroupId;
  const shareDetails = collection?.shareDetails;
  const ticker = collection?.ticker;
  const exchange = collection?.stockExchange;
  const collectionConversationId = collection?.conversationId;
  const dashboardMetricsData = useDashboardMetrics(projectMetadataIds, "buy_sell");
  const buySellValue = dashboardMetricsData ? (dashboardMetricsData[0] as DashboardMetric) : undefined;
  const buySellScoreSentiment = useCollectionKey(collectionId, "buySellScoreSentiment");
  const legacyBuySellScore = buySellScoreSentiment?.value;
  const buySellValueToUse = buySellValue?.data?.[0] || Number(legacyBuySellScore);
  const numericRating = parseBuySellRatingNumber(buySellValueToUse);
  const chartColors = useBuySellChartColors();
  const ratingConfig = useMemo(() => createRatingConfig(chartColors), [chartColors]);
  const buySellData = getRatingInfo(numericRating, ratingConfig);

  const companyLogoData = useContents(projectMetadataIds, {
    refreshFromNetwork: true,
    filterByTags: ["company_logo"],
  });

  const currentWorkflowId = useLatestCollectionWorkflowId(collectionId);
  const currentWorkflowCompletionDate = useWorkflowCompletionDateOrNow(currentWorkflowId);
  const configMap = useConfigMap();

  // TODO: change this to use tags
  const companyStockContentData = useContents(projectMetadataIds, {
    refreshFromNetwork: true,
    filterByName: "stock equity data",
  });

  const companyStockData = useMemo((): StockEquityData | null => {
    if (companyStockContentData && companyStockContentData.length > 0) {
      const dynamicData = companyStockContentData[0].dynamicData?.dynamic_data;

      if (!dynamicData) return null;

      if (dynamicData && typeof dynamicData === "object" && "data" in dynamicData) {
        return dynamicData as StockEquityData;
      }

      return null;
    }
    return null;
  }, [companyStockContentData]);

  const companyLongName = companyStockData?.data.chart.result[0].meta.longName || "";

  const projectCreationDate = collection?.metadata?.createdTime;
  const placeholderStockEquityData = useMemo(() => generateMockStockEquityData(), []);
  const isPlaceholderTile = collectionId === "placeholder";
  const portfoliosProjectIsIn = useProjectInPortfolios(collectionId);
  const collectionQuestions = useCollectionQuestionsFromReduxStore(collectionId);
  const isSharedRead = shareDetails && shareDetails.accessMode === "read";
  const { setSelectedProjectId, deletedCollectionIds } = useContext(CollectionsFilterContext);
  const { onConversationOpen, onConversationClose, onDeleteProjectModalOpen, setShowConversationMessages } = useConversationContext();
  const { setIsSideNavigationOpen } = useSettingContext();

  // Check if this collection has been deleted
  const isDeleted = useMemo(() => {
    return deletedCollectionIds?.includes(collectionId);
  }, [deletedCollectionIds, collectionId]);
  const { isOpen: isPanelOpen, onOpen: onPanelOpen, onClose: onPanelClose } = useDisclosure();
  const { onOpen: onOpenMoveProject, onClose: onCloseMoveProject, isOpen: isOpenMoveProject } = useDisclosure();

  // Use the project move hook for remove functionality
  const { removeFromPortfolio, isInPortfolio } = useProjectMove({
    collectionId,
    conversationId: collectionConversationId || uuid(),
  });
  const placeholderTileText = useCopyValue("copy_project_placeholder_message");
  const loadingText = useCopyValue("copy_project_starting_message");
  const workflowStatus = useWorkflowKey(currentWorkflowId, "status");

  const contentDataProjectReports = useContents(projectMetadataIds ?? [], { refreshFromNetwork: false, filterByTags: ["project_output"] });
  const contentDataFiltered = useFilterAndGroupReports(contentDataProjectReports);
  const firstContentDetailFromFilter = useMemo(() => {
    const firstGroup = Object.values(contentDataFiltered)[0];
    return firstGroup?.[0] as ContentDetails | undefined;
  }, [contentDataFiltered]);

  const oneTimeSchedule = useWorkflowScheduleByProject(collectionId);
  const hasOneTimeSchedule = useMemo(() => !!oneTimeSchedule, [oneTimeSchedule]);

  const businessOverview = useMemo(() => {
    return collectionQuestions[0]?.answers?.[0]?.answer || undefined;
  }, [collectionQuestions]);

  const tileRef = useRef<HTMLDivElement>(null);
  const { scrollbarStyle } = useCustomScrollbar(tileRef, { width: "1px" });

  const handleActionClick = useCallback((actionType: string, collectionId: string) => {
    // Don't close the modal on action clicks
    return false;
  }, []);

  const handlePanelToggle = useCallback(() => {
    isPanelOpen ? onPanelClose() : onPanelOpen();
    return false;
  }, [isPanelOpen, onPanelOpen, onPanelClose]);

  const getPath = () => {
    const projectRoute = getViewConfig("route", collectionType || "", configMap);
    const path = parentRoute === "home" ? `/${projectRoute}/${collectionId}` : `/${parentRoute}/${projectFilter}/${collectionId}`;
    if (projectRoute) {
      onConversationClose();
      return navigate(path);
    } else {
      setShowConversationMessages(true);
      onConversationOpen();
    }
  };

  const { id: currentUserId } = useUserProfile();
  const isSharedOwner = useMemo(() => {
    if (!shareDetails) {
      return true;
    }
    return shareDetails?.ownerId === currentUserId;
  }, [currentUserId, shareDetails]);

  // Don't render if the collection has been deleted
  if (isDeleted) {
    return null;
  }

  return (
    <>
      <Box
        {...commonTileProps}
        bgColor={tileBgColor}
        p="0"
        draggable={!isPlaceholderTile && !isSharedRead && workflowStatus !== "in_progress"}
        onDragStart={(e) => {
          if (!isPlaceholderTile && !isSharedRead && workflowStatus !== "in_progress") {
            e.dataTransfer.setData("text/plain", `project-tile-${collectionId}`);
            e.dataTransfer.setData(
              "application/json",
              JSON.stringify({
                collectionId,
                conversationId: collectionConversationId || uuid(),
              })
            );
            e.dataTransfer.effectAllowed = "move";

            (e.currentTarget as HTMLElement).style.opacity = "0.5";
            (e.currentTarget as HTMLElement).style.borderWidth = "3px";
            (e.currentTarget as HTMLElement).style.borderStyle = "dashed";
            (e.currentTarget as HTMLElement).style.transform = "scale(0.97)";
            (e.currentTarget as HTMLElement).style.transition = "all 0.2s ease";

            const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
            const isChrome = navigator.userAgent.toLowerCase().includes("chrome") && !navigator.userAgent.toLowerCase().includes("firefox");

            // Add global class to indicate dragging state and store dragged project ID
            document.body.classList.add("project-tile-dragging");
            document.body.setAttribute("data-dragged-project-id", collectionId);

            // Firefox-specific: Force a reflow to ensure CSS changes are applied immediately
            if (isFirefox) {
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              document.body.offsetHeight;
              setTimeout(() => {
                document.body.classList.add("project-tile-dragging");
              }, 1);
            }

            if (isChrome) {
              setTimeout(() => {
                setIsSideNavigationOpen(true);
              }, 10);
            } else {
              setIsSideNavigationOpen(true);
              if (isFirefox) {
                setTimeout(() => {
                  setIsSideNavigationOpen(true);
                }, 5);
              }
            }

            const dragElement = e.currentTarget as HTMLElement;
            const logoElement = dragElement.querySelector("img") || dragElement.querySelector('[data-testid="project-header-image"]');

            if (logoElement && logoElement instanceof HTMLImageElement) {
              try {
                const rect = logoElement.getBoundingClientRect();
                const padding = 8;
                const borderRadius = 8;

                if (isFirefox) {
                  // Firefox: Use canvas with styled container
                  const canvas = document.createElement("canvas");
                  const ctx = canvas.getContext("2d");

                  if (ctx) {
                    const minWidth = 160;
                    const containerWidth = Math.max(rect.width + padding * 2, minWidth);
                    const containerHeight = rect.height + padding * 2;

                    canvas.width = containerWidth;
                    canvas.height = containerHeight;

                    ctx.fillStyle = tileBgColor;
                    ctx.strokeStyle = tileBorderColor;
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.roundRect(0, 0, containerWidth, containerHeight, borderRadius);
                    ctx.fill();
                    ctx.stroke();

                    const logoX = (containerWidth - rect.width) / 2;
                    ctx.drawImage(logoElement, logoX, padding, rect.width, rect.height);

                    e.dataTransfer.setDragImage(canvas, containerWidth / 2, containerHeight / 2);
                  } else {
                    e.dataTransfer.setDragImage(logoElement, rect.width / 2, rect.height / 2);
                  }
                } else {
                  const minWidth = 160;
                  const containerWidth = Math.max(rect.width + padding * 2, minWidth);
                  const containerHeight = rect.height + padding * 2;

                  const dragContainer = document.createElement("div");
                  dragContainer.style.position = "absolute";
                  dragContainer.style.top = "-1000px";
                  dragContainer.style.left = "-1000px";
                  dragContainer.style.backgroundColor = tileBgColor;
                  dragContainer.style.border = `1px solid ${tileBorderColor}`;
                  dragContainer.style.borderRadius = `${borderRadius}px`;
                  dragContainer.style.padding = `${padding}px`;
                  dragContainer.style.pointerEvents = "none";
                  dragContainer.style.zIndex = "-1";
                  dragContainer.style.width = `${containerWidth}px`;
                  dragContainer.style.height = `${containerHeight}px`;
                  dragContainer.style.opacity = "1";
                  dragContainer.style.display = "flex";
                  dragContainer.style.alignItems = "center";
                  dragContainer.style.justifyContent = "center";

                  // Clone and style the logo
                  const clonedImg = logoElement.cloneNode(true) as HTMLImageElement;
                  clonedImg.style.width = `${rect.width}px`;
                  clonedImg.style.height = `${rect.height}px`;
                  clonedImg.style.display = "block";
                  clonedImg.style.opacity = "1";

                  dragContainer.appendChild(clonedImg);
                  document.body.appendChild(dragContainer);

                  // Use the styled container as drag image
                  e.dataTransfer.setDragImage(dragContainer, containerWidth / 2, containerHeight / 2);

                  // Clean up after drag starts
                  setTimeout(() => {
                    if (document.body.contains(dragContainer)) {
                      document.body.removeChild(dragContainer);
                    }
                  }, 100);
                }
              } catch (error) {
                console.warn("Could not set logo as drag image:", error);
                const tileRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                e.dataTransfer.setDragImage(e.currentTarget as HTMLElement, tileRect.width / 2, tileRect.height / 2);
              }
            } else {
              const tileRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              e.dataTransfer.setDragImage(e.currentTarget as HTMLElement, tileRect.width / 2, tileRect.height / 2);
            }
          }
        }}
        onDragEnd={(e) => {
          // Reset opacity when drag ends
          (e.currentTarget as HTMLElement).style.opacity = "1";
          (e.currentTarget as HTMLElement).style.borderWidth = "1px";
          (e.currentTarget as HTMLElement).style.borderStyle = "solid";

          // Remove global dragging class and data attribute
          document.body.classList.remove("project-tile-dragging");
          document.body.removeAttribute("data-dragged-project-id");

          setIsSideNavigationOpen(false);
        }}
        onClick={() => {
          if (!isPlaceholderTile) {
            getPath();
          }
        }}
        onMouseEnter={() => setSelectedProjectId(collectionId)}
        overflow="visible"
        textAlign="left"
        className={`${
          isPlaceholderTile ? "ch-project-landing-placeholder-tile" : `ch-project-landing-default-${isSharedRead ? "shared" : "tile"}`
        }`}
        cursor="pointer">
        <Stack spacing={"0"} direction="column" h="full">
          <Stack spacing={isMobile ? "0.5rem" : "1rem"} direction="column" h="full" p={isMobile ? "0.5rem" : "1rem"}>
            <Stack direction="row" justifyContent={"space-between"} width="100%" alignItems="center">
              {(companyLongName.length > 0 || projectName) && (
                <Stack direction={"row"} spacing={"0.5rem"} alignItems="center" maxHeight={"20px"}>
                  {currentWorkflowId && (
                    <Stack direction={"row"} spacing={"0.5rem"} alignItems="center">
                      <Center>
                        <WorkflowProgressBar
                          workflowId={currentWorkflowId}
                          size="compact"
                          lastUpdated={formatDistanceToNow(new Date(currentWorkflowCompletionDate), {
                            addSuffix: true,
                            includeSeconds: false,
                          })}
                          progressIndicatorStyle="circle"
                          conversationId={collectionConversationId}
                        />
                      </Center>
                    </Stack>
                  )}
                  <TextOverflowTooltip
                    label={companyLongName ? companyLongName : projectName?.toLowerCase() === "new project" ? "" : projectName || ""}
                    color={textColor}
                    noOfLines={1}
                    className="ch-project-landing-title"
                  />
                  <Text fontSize="xs" color={textColor}>
                    ({exchange}:{ticker})
                  </Text>
                </Stack>
              )}
              {!isPortfolios &&
                portfoliosProjectIsIn &&
                portfoliosProjectIsIn.projects.map((portfolio, index) => {
                  return (
                    <Tooltip
                      key={index}
                      label={`Open  ${shareDetails?.accessMode === "read" ? "shared" : ""} portfolio ${portfolio?.name}`}
                      aria-label="Portfolio Name"
                      placement="top">
                      <Tag
                        className="ch-project-landing-portfolio-tag"
                        minHeight={"20px"}
                        paddingInline={"5px"}
                        zIndex={2}
                        variant="subtle"
                        borderWidth="1px"
                        borderColor={tagBorderColor}
                        bgColor={tagBgColor}
                        color={tagColor}
                        fontSize="0.65rem"
                        cursor={"pointer"}
                        onClick={(evt) => {
                          evt.stopPropagation();
                          navigate(`/portfolios/${portfolio?.id}`);
                        }}>
                        <Icon as={shareDetails?.accessMode === "read" ? FiShare2 : GrLineChart} boxSize=".8rem" color={tagColor} mr="5px" />
                        {portfolio?.name}
                      </Tag>
                    </Tooltip>
                  );
                })}
            </Stack>
            <Stack direction={"row"} spacing={"1rem"} alignItems="top" justifyContent={"space-between"}>
              <Stack minWidth={isMobile ? "7rem" : "10rem"} overflow={"hidden"}>
                {isPlaceholderTile && (
                  <Button
                    {...primaryButtonProps}
                    _hover={{}}
                    _active={{}}
                    leftIcon={<Image src={dealsIcon} boxSize="1rem" pb="1px" />}
                    onClick={() => navigate("/deal-finder")}>
                    Deal Finder
                  </Button>
                )}
                <Stack spacing={"1.5rem"} alignItems="left" position={"relative"}>
                  {logoUrl ? (
                    <Box minWidth="10rem">
                      <Image src={logoUrl} alt={`company logo`} maxH={isMobile ? "25px" : "30px"} />
                    </Box>
                  ) : (
                    <ProjectHeaderImage
                      maxHeight={"2rem"}
                      width="unset"
                      collectionId={collectionId}
                      projectName={
                        companyLongName ? companyLongName : projectName?.toLowerCase() === "new project" ? "" : projectName || ""
                      }
                      projectType={collectionType}
                      projectGroupId={projectGroupId}
                      logoOnly
                      mediaId={companyLogoData?.[0]?.id}
                    />
                  )}
                  {buySellData.label !== "--" && (
                    <Box
                      position={"absolute"}
                      top={isMobile ? "2.5rem" : "3.3rem"}
                      bgColor={buySellData.color}
                      textAlign={"center"}
                      maxWidth="7.5rem"
                      color={textColor}
                      borderRadius="md"
                      px="4px"
                      py="2px"
                      fontSize={isMobile ? "10px" : "xs"}
                      fontWeight={"semibold"}
                      width="100%">
                      {buySellData.label.toUpperCase()}
                    </Box>
                  )}
                </Stack>
              </Stack>
              <Box color={textColor} width="100%">
                <ShowMoreContainer minHeight="6.5rem" height="4.5rem">
                  <MarkdownItemView markdown={isPlaceholderTile ? placeholderTileText : businessOverview || loadingText} />
                </ShowMoreContainer>
              </Box>
            </Stack>
            {(companyStockData || (isSharedRead && firstContentDetailFromFilter)) && (
              <Stack direction="row" justify="space-between" align="center" spacing="1rem">
                {isSharedRead && firstContentDetailFromFilter && (
                  <Box position="relative" className="ch-project-landing-report-preview-container">
                    <ProjectAnalysisPopover projectId={collectionId}>
                      <Box borderColor={tileBorderColor} borderWidth="1px" borderRadius="sm" width="4rem">
                        <ContentPreview
                          thumbnailFit="fill"
                          maxHeight="4.9rem"
                          backgroundColor={"transparent"}
                          itemContent={firstContentDetailFromFilter}
                          thumbnailHeight={"100px"}
                          noOnClickAction
                        />
                      </Box>
                    </ProjectAnalysisPopover>
                  </Box>
                )}
                {companyStockData && (
                  <Tooltip
                    zIndex={2}
                    aria-label="stock-equity-tooltip"
                    label={`As of ${
                      projectCreationDate && formatDateWithOutputFormat(new Date(projectCreationDate), "do MMM yyyy hh:mm")
                    }`}>
                    <Stack
                      direction="row"
                      borderColor={borderColor}
                      borderWidth={"1px"}
                      borderRadius={"md"}
                      px="1rem"
                      py=".5rem"
                      zIndex={1}
                      width="100%">
                      <StockEquityChart
                        height="4rem"
                        hideAxis
                        hideBorder
                        stockEquityData={companyStockData || placeholderStockEquityData}
                        usingMockData={!companyStockData}
                      />
                      <Center>
                        <StockIndicatorSmall ticker={ticker} exchange={exchange} />
                      </Center>
                    </Stack>
                  </Tooltip>
                )}
              </Stack>
            )}
            {(currentWorkflowId && isPlaceholderTile) ||
              (workflowStatus === "in_progress" && (
                <Box
                  zIndex={1}
                  position={"absolute"}
                  width="100%"
                  left="0"
                  bottom="2.7rem"
                  borderColor={tileBorderColor}
                  borderTopWidth="1px"
                  p="10px"
                  bgColor={tileBgColor}>
                  <WorkflowProgressStepperContainer
                    hasOneTimeSchedule={hasOneTimeSchedule}
                    oneTimeScheduleDesiredExecutionDate={oneTimeSchedule?.desiredExecutionDate}
                    collectionId={collectionId}
                    hideProgressBar={false}
                  />
                </Box>
              ))}
          </Stack>
          <Stack
            direction="row"
            width="100%"
            height="2rem"
            justify="space-between"
            spacing={isMobile ? ".5rem" : "1rem"}
            px={isMobile ? ".5rem" : "1rem"}
            py={isMobile ? ".2rem" : ".5rem"}
            borderTop="1px solid"
            borderColor={borderColor}>
            <Stack direction="row" justify="space-between" spacing={"1rem"}>
              <SmallActionButton
                classNames={previousVersionCount && previousVersionCount > 0 ? "ch-rerun-version-tag" : ""}
                tooltip={previousVersionCount && previousVersionCount > 0 ? `${previousVersionCount} Previous Versions` : ""}
                // isDisabled={!isSharedOwner || isPlaceholderTile || workflowStatus === "in_progress" || previousVersionCount === 0}
                isDisabled
                iconSize="1.2rem"
                iconTypeName={TbVersions}
                onClick={() => handlePanelToggle()}
              />
              <SmallActionButton
                isDisabled={!isSharedOwner || isPlaceholderTile || workflowStatus === "in_progress"}
                iconSize="1rem"
                tooltip="Like"
                iconTypeName={BiLike}
                onClick={() => handleActionClick("like", collectionId)}
              />
              <Tooltip label="Coming Soon" placement="top" aria-label="Like">
                <Stack>
                  <SmallActionButton
                    // isDisabled={!isSharedOwner || isPlaceholderTile || workflowStatus === "in_progress"}
                    isDisabled
                    iconSize="1rem"
                    tooltip="Share"
                    iconTypeName={BiShareAlt}
                    onClick={() => handleActionClick("share", collectionId)}
                  />
                </Stack>
              </Tooltip>
            </Stack>
            <Stack direction="row" justify="space-between" spacing={"1rem"}>
              <ProjectRerunCancelButton isDisabled={!isSharedOwner || isPlaceholderTile} reRunProjectId={collectionId} isIcon />
              <ProjectReportPanelButton
                showFirstReport
                isDisabled={!isSharedOwner || isPlaceholderTile || workflowStatus === "in_progress"}
                collectionId={collectionId}
              />
              <Menu isLazy placement="bottom-end">
                <MenuButton
                  height="0.9rem"
                  width="0.5rem"
                  minWidth={"unset"}
                  paddingInline={0}
                  border="none"
                  _hover={{
                    color: "primary.default",
                  }}
                  _active={{}}
                  color={defaultIconColor}
                  bgColor={"transparent"}
                  className="ch-content-options"
                  as={Button}
                  aria-label="Options"
                  rightIcon={<BiDotsVertical color={defaultIconColor} />}
                  onClick={(event) => event.stopPropagation()}></MenuButton>
                <Portal>
                  <MenuList minWidth={"unset"} zIndex={10} boxShadow={"md"}>
                    <ProjectDeleteButton
                      isDisabled={!isSharedOwner || isPlaceholderTile}
                      label={"Delete Project"}
                      onOpenConfirmation={onDeleteProjectModalOpen}
                      projectId={collectionId}
                      projectType={"project"}
                      isMenuItem
                    />
                    <ProjectMoveButton
                      isDisabled={!isSharedOwner || isPlaceholderTile || workflowStatus === "in_progress"}
                      isMenuItem
                      onClickMove={onOpenMoveProject}
                      onClickRemove={isInPortfolio ? removeFromPortfolio : undefined}
                    />
                  </MenuList>
                </Portal>
              </Menu>
            </Stack>
          </Stack>
        </Stack>
        <Box
          position="absolute"
          bottom="2px"
          left="2px"
          right="2px"
          height={isPanelOpen ? "87%" : "0"}
          bgColor={panelBgColor}
          borderTopRadius="lg"
          boxShadow="0 -4px 6px -1px rgba(0, 0, 0, 0.1)"
          transition="height 0.3s ease-in-out"
          overflow="hidden"
          zIndex={10}>
          <SlideFade in={isPanelOpen} offsetY="20px">
            <Box p="1rem" overflowY="hidden">
              <Stack direction="row" justify="space-between" align="center" pb="1rem">
                <Heading size="sm">{`${previousVersionCount} Previous Versions`}</Heading>
                <IconButton
                  icon={<CloseIcon />}
                  _hover={{}}
                  _active={{}}
                  aria-label="Close panel"
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    onPanelClose();
                    e.stopPropagation();
                  }}
                />
              </Stack>
              <Box height="15rem" overflowX="hidden" overflowY={"auto"} mr="2px" css={scrollbarStyle}>
                {previousVersions}
              </Box>
            </Box>
          </SlideFade>
        </Box>
      </Box>
      <MoveProjectModal
        isOpen={isOpenMoveProject}
        onClose={() => {
          onCloseMoveProject();
        }}
        collectionId={collectionId}
        conversationId={collectionConversationId || uuid()}
      />
    </>
  );
};

export default ProjectLandingSmallTile;
